'use client';

import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { useIntl } from 'react-intl';
import { unmask } from '@packages/gql/src/betterMasking';
import type { EcoLabelItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/EcoLabelItemFragmentFragmentDoc';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { useState } from 'react';
import { useServiceLayerTracking } from '../Tracking/hooks/useServiceLayerTracking';
import { useEcoLabelItemTracking } from './Tracking/useEcoLabelItemTracking/useEcoLabelItemTracking';
import { ecoLabelItemMessages } from './messages';

/* GraphQL */ `
  fragment EcoLabelItemFragment on EcoLabel {
    iconUrl
    text
    serviceLayerId
    license
  }
`;

export type EcoLabelProps = {
  ecoLabelData: FragmentType<typeof EcoLabelItemFragmentFragmentDoc>;
  /**
   * If `ecoLabelData['text']` is `null`, this fallback is used instead.
   */
  fallbackHeadline?: string;

  mode?: 'normal' | 'productStandard';
};

/**
 * <p>This component renders one eco-attribute of a product</p>
 * <i>(e.G.: "Bio Baumwolle", "Grüner Knopf" or "GOTS organic").</i>
 * */
export const EcoLabelItem = ({
  fallbackHeadline,
  ecoLabelData,
  mode = 'normal',
}: EcoLabelProps) => {
  const { iconUrl, license, serviceLayerId, text } =
    unmask<typeof EcoLabelItemFragmentFragmentDoc>(ecoLabelData);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { formatMessage } = useIntl();
  const { trackServiceLayerClick } = useServiceLayerTracking();
  const { trackEcoLabelDisplayOverlayed } = useEcoLabelItemTracking();

  // if iconUrl, license and text is missing we can not render the component
  // because it would have no content to display
  if (!iconUrl && !license && !text) {
    return null;
  }

  const openDialogHandler = () => {
    trackServiceLayerClick(serviceLayerId!);
    trackEcoLabelDisplayOverlayed(text || fallbackHeadline || serviceLayerId || undefined);

    setIsModalOpen(true);
  };

  const closeDialogHandler = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className={`flex w-full items-center gap-1.5 text-left ${mode === 'productStandard' ? 'max-w-4' : ''}`}
        onClick={serviceLayerId ? openDialogHandler : undefined}
        disabled={!serviceLayerId}
      >
        {iconUrl && (
          <Image
            width={mode === 'normal' ? 52 : 32}
            height={mode === 'normal' ? 52 : 32}
            alt={text || ''}
            src={iconUrl}
            unoptimized
            className="max-h-6.5 max-w-6.5 object-contain"
          />
        )}
        <span className="flex flex-col">
          {text && <span className="text-size-body2 underline">{text}</span>}
          {license && (
            <span className="text-size-body3 text-color-text-darktransparent">{license}</span>
          )}
        </span>
      </button>
      {serviceLayerId && (
        <Dialog isOpen={isModalOpen} onClose={closeDialogHandler}>
          <DialogHeader onClose={closeDialogHandler}>
            {mode === 'normal'
              ? text || fallbackHeadline
              : formatMessage(ecoLabelItemMessages.ecoabelProductStandardDefaultTitle)}
          </DialogHeader>
          <DialogContent>
            <ServiceLayer serviceLayerId={serviceLayerId} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
